import { all, call, put, takeEvery } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { IRequestParams, IResponse } from '@dash/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import notifications from 'src/redux/notifications/actions';
import { logger } from 'src/helpers/logger';
import ApiInstance from 'src/helpers/apiInstance';
import { t } from 'src/i18n';
import {
  LoadReasonsAction,
  LoadConversionAction,
  InitiateConversionAction,
  DeclineConversionAction,
} from './actions.d';
import actions from './actions';
import actionTypes from './actionTypes';
import { IPredefinedReasonsResponse, IDayforceConversionResponse, IConversionMutationResponse } from '../types';

const CONVERSION_PATH = 'agencies/worker_identities/:worker_identity_id/dayforce_conversion';

function getValidationErrors(result: any): string | null {
  if (result?.validationMessages?.length) {
    return result.validationMessages.join(', ');
  }

  const validations = result?.data?.validations;

  if (validations) {
    return Object.values(validations).flat().join(', ');
  }

  return result?.messages?.join(', ') || null;
}

const reasonsApi = new ApiInstance('predefined_reasons');
const conversionApi = new ApiInstance(CONVERSION_PATH);
const declineApi = new ApiInstance(`${CONVERSION_PATH}/decline`);

function* loadReasons({ payload }: LoadReasonsAction): Generator<any, void, any> {
  try {
    const { categories } = payload;

    const result: IResponse<IPredefinedReasonsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      reasonsApi.get,
      { search: { categories } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      const { predefined_reasons } = result.data;
      yield put(actions.loadReasonsSuccess({ reasons: predefined_reasons }));
    } else {
      yield put(actions.loadReasonsError());
    }
  } catch (err) {
    yield put(actions.loadReasonsError());
    logger.error(err);
  }
}

function* loadConversion({ payload }: LoadConversionAction): Generator<any, void, any> {
  try {
    const { worker_identity_id } = payload;

    const result: IResponse<IDayforceConversionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      conversionApi.get,
      { pathParams: { worker_identity_id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      yield put(
        actions.loadConversionSuccess({
          conversion: result.data.conversion,
          ineligible_reason: result.data.ineligible_reason,
        }),
      );
    } else {
      yield put(actions.loadConversionError());
    }
  } catch (err) {
    yield put(actions.loadConversionError());
    logger.error(err);
  }
}

function* initiateConversion({ payload }: InitiateConversionAction): Generator<any, void, any> {
  try {
    const { worker_identity_id, reason_id, position_id, legal_entity_id } = payload;

    const result: IResponse<IConversionMutationResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      conversionApi.post,
      {
        pathParams: { worker_identity_id },
        data: { reason_id, position_id, legal_entity_id },
      },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 201 && result.data) {
      yield put(actions.initiateConversionSuccess({ conversion: result.data.conversion }));
    } else {
      const errorMessage = getValidationErrors(result);

      if (errorMessage) {
        yield put(notifications.addNotification({ type: 'error', message: errorMessage }));
      }
      yield put(actions.initiateConversionError());
    }
  } catch (err) {
    yield put(actions.initiateConversionError());
    logger.error(err);
  }
}

function* declineConversion({ payload }: DeclineConversionAction): Generator<any, void, any> {
  try {
    const { worker_identity_id, worker_name, reason_id } = payload;

    const result: IResponse<IConversionMutationResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      declineApi.post,
      {
        pathParams: { worker_identity_id },
        data: { reason_id },
      },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 201 && result.data) {
      yield put(actions.declineConversionSuccess({ conversion: result.data.conversion }));
      yield put(
        notifications.addNotification({
          type: 'success',
          message: t('dash.agencies.convertToPermanent.decline_success', { worker_name }),
        }),
      );
      yield put(push(`/agencyWorkers/${worker_identity_id}`));
    } else {
      const errorMessage = getValidationErrors(result);

      if (errorMessage) {
        yield put(notifications.addNotification({ type: 'error', message: errorMessage }));
      }
      yield put(actions.declineConversionError());
    }
  } catch (err) {
    yield put(actions.declineConversionError());
    logger.error(err);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.LOAD_REASONS, loadReasons),
    takeEvery(actionTypes.LOAD_CONVERSION, loadConversion),
    takeEvery(actionTypes.INITIATE_CONVERSION, initiateConversion),
    takeEvery(actionTypes.DECLINE_CONVERSION, declineConversion),
  ]);
}
